import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Mail, Calendar, MessageSquare, CheckCircle, Circle,
    ChevronRight, Loader2, Coffee, Sparkles, AlertTriangle,
    ExternalLink
} from 'lucide-react';

/**
 * OnboardingMessage Component
 * 
 * Renders different types of onboarding messages in the chat:
 * - Welcome: Initial greeting with start button
 * - Connect: OAuth connection prompts
 * - Progress: Animated progress bar during ingestion
 * - Question: Interactive forms for preferences
 * - Complete: Success summary
 * - Reminder: Gentle nudge to continue
 */
const OnboardingMessage = ({
    message,
    darkMode,
    onAction,
    progress = null  // For live progress updates
}) => {
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');

    if (!message) return null;

    const { type, title, content, buttons, options, show_skip, progress: msgProgress } = message;

    // Use live progress if available, otherwise message progress
    const currentProgress = progress || msgProgress;

    const handleOptionToggle = (value) => {
        if (selectedOptions.includes(value)) {
            setSelectedOptions(selectedOptions.filter(v => v !== value));
        } else {
            setSelectedOptions([...selectedOptions, value]);
        }
    };

    const handleButtonClick = (action) => {
        // For questions, pass selected options
        if (type === 'question' && selectedOptions.length > 0) {
            onAction(action, selectedOptions);
        } else if (type === 'question' && inputValue) {
            onAction(action, inputValue);
        } else {
            onAction(action);
        }
    };

    // Icon for connection types
    const getConnectionIcon = (step) => {
        if (step?.includes('gmail')) return <Mail className="text-red-500" size={24} />;
        if (step?.includes('slack')) return <MessageSquare className="text-purple-500" size={24} />;
        if (step?.includes('calendar')) return <Calendar className="text-blue-500" size={24} />;
        return <Sparkles className="text-yellow-500" size={24} />;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-2xl p-6 mb-4 ${darkMode
                    ? 'bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10'
                    : 'bg-gradient-to-br from-white to-slate-50 border border-slate-200 shadow-lg'
                }`}
        >
            {/* Header */}
            {title && (
                <div className="flex items-center gap-3 mb-4">
                    {type === 'welcome' && <Sparkles className="text-yellow-500" size={28} />}
                    {type === 'connect' && getConnectionIcon(message.step)}
                    {type === 'progress' && <Loader2 className="text-blue-500 animate-spin" size={24} />}
                    {type === 'complete' && <CheckCircle className="text-green-500" size={28} />}
                    {type === 'reminder' && <AlertTriangle className="text-amber-500" size={24} />}

                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {title}
                    </h3>
                </div>
            )}

            {/* Content */}
            <div className={`mb-4 whitespace-pre-line ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                {content}
            </div>

            {/* Progress Bar (for ingestion) */}
            {type === 'progress' && currentProgress && (
                <ProgressBar
                    current={currentProgress.current}
                    total={currentProgress.total}
                    percentage={currentProgress.percentage}
                    eta={currentProgress.eta_seconds}
                    insights={currentProgress.insights}
                    darkMode={darkMode}
                />
            )}

            {/* Options (for questions) */}
            {type === 'question' && options && (
                <div className="space-y-2 mb-4">
                    {options.map((opt, idx) => (
                        <motion.button
                            key={idx}
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            onClick={() => handleOptionToggle(opt.value || opt.email)}
                            className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${selectedOptions.includes(opt.value || opt.email)
                                    ? darkMode
                                        ? 'bg-blue-500/20 border-blue-500 border'
                                        : 'bg-blue-50 border-blue-500 border'
                                    : darkMode
                                        ? 'bg-white/5 border-white/10 border hover:bg-white/10'
                                        : 'bg-white border-slate-200 border hover:bg-slate-50'
                                }`}
                        >
                            {selectedOptions.includes(opt.value || opt.email) ? (
                                <CheckCircle className="text-blue-500" size={20} />
                            ) : (
                                <Circle className={darkMode ? 'text-slate-500' : 'text-slate-400'} size={20} />
                            )}
                            <div className="flex-1 text-left">
                                <span className={darkMode ? 'text-white' : 'text-slate-900'}>
                                    {opt.label || opt.name}
                                </span>
                                {opt.count && (
                                    <span className={`ml-2 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        ({opt.count} emails)
                                    </span>
                                )}
                            </div>
                        </motion.button>
                    ))}
                </div>
            )}

            {/* Buttons */}
            {buttons && buttons.length > 0 && (
                <div className="flex flex-wrap gap-3">
                    {buttons.map((btn, idx) => (
                        <motion.button
                            key={idx}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleButtonClick(btn.action)}
                            className={`px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-all ${btn.primary
                                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40'
                                    : darkMode
                                        ? 'bg-white/10 text-white hover:bg-white/20'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                        >
                            {btn.label}
                            {btn.primary && <ChevronRight size={18} />}
                        </motion.button>
                    ))}
                </div>
            )}
        </motion.div>
    );
};

/**
 * Animated Progress Bar Component
 */
const ProgressBar = ({ current, total, percentage, eta, insights, darkMode }) => {
    return (
        <div className="mb-4">
            {/* Progress bar */}
            <div className={`h-3 rounded-full overflow-hidden mb-2 ${darkMode ? 'bg-white/10' : 'bg-slate-200'
                }`}>
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                />
            </div>

            {/* Stats */}
            <div className="flex justify-between text-sm mb-3">
                <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                    {current}/{total} processed
                </span>
                {eta > 0 && (
                    <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                        ~{Math.ceil(eta / 60)} min remaining
                    </span>
                )}
            </div>

            {/* Fun message */}
            <div className={`flex items-center gap-2 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <Coffee size={16} />
                <span>Feel free to grab coffee ☕</span>
            </div>

            {/* Insights */}
            {insights && insights.length > 0 && (
                <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-white/5' : 'bg-blue-50'}`}>
                    <div className={`text-xs uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        What I'm finding:
                    </div>
                    {insights.slice(-3).map((insight, idx) => (
                        <div key={idx} className={`text-sm flex items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <Sparkles size={14} className="text-yellow-500" />
                            {insight}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Onboarding Banner (for incomplete onboarding reminder)
 */
export const OnboardingBanner = ({ state, darkMode, onResume }) => {
    if (!state || state.onboarding_complete || state.skipped) return null;

    const getProgress = () => {
        let done = 0;
        if (state.accounts_connected?.gmail) done++;
        if (state.emails_ingested >= 500) done++;
        if (state.accounts_connected?.slack) done++;
        if (state.questions_answered?.role) done++;
        return done;
    };

    return (
        <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className={`mx-4 mb-4 p-3 rounded-xl flex items-center justify-between ${darkMode
                    ? 'bg-amber-500/10 border border-amber-500/20'
                    : 'bg-amber-50 border border-amber-200'
                }`}
        >
            <div className="flex items-center gap-3">
                <AlertTriangle className="text-amber-500" size={20} />
                <span className={darkMode ? 'text-amber-200' : 'text-amber-800'}>
                    Setup {getProgress()}/4 complete
                </span>
            </div>
            <button
                onClick={onResume}
                className="px-3 py-1 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-colors"
            >
                Continue →
            </button>
        </motion.div>
    );
};

export default OnboardingMessage;
